import React, { useEffect, useState, useCallback } from 'react';
import { api, formatApiError } from '../../lib/api';
import { useDarkMode } from '../../context/DarkModeContext';
import { Plus, Trash2, Globe, Building2 } from 'lucide-react';

const TIERS = ['Tier 1', 'Tier 2', 'Tier 3', 'Unranked'];
const TIER_COLOR = { 'Tier 1': '#64cb13', 'Tier 2': '#ff9200', 'Tier 3': '#8a5cf6', Unranked: '#7a8699' };

const YesNo = ({ value }) => (
  <span className="tag-pill" style={value ? { background: '#64cb1322', color: '#64cb13' } : { background: 'var(--surface-2)', color: 'var(--text-faded)' }}>
    {value ? 'Yes' : 'No'}
  </span>
);

const Companies = ({ lob }) => {
  const darkMode = useDarkMode();
  const aBg = darkMode ? lob?.accentBgDark : lob?.accentBg;
  const aTc = darkMode ? lob?.accentTcDark : lob?.accentTc;
  const [companies, setCompanies] = useState([]);
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [editing, setEditing] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(() => {
    if (!lob) return;
    setLoading(true); setErr('');
    api.get(`/companies?lob_id=${lob.id}${search ? `&q=${encodeURIComponent(search)}` : ''}${tierFilter ? `&tier=${encodeURIComponent(tierFilter)}` : ''}`)
      .then((r) => setCompanies(r.data))
      .catch((e) => setErr(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [lob?.id, search, tierFilter]);

  useEffect(() => { load(); }, [load]);

  const openEdit = (c) => { setEditing(c); setShowModal(true); };
  const openNew = () => { setEditing(null); setShowModal(true); };

  const del = async (c) => {
    if (!window.confirm(`Delete ${c.name}?`)) return;
    try { await api.delete(`/companies/${c.id}`); load(); } catch (e) { alert(formatApiError(e)); }
  };

  return (
    <>
      <div className="page-toolbar" data-testid="companies-toolbar">
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span className="page-title">Companies</span>
          <span className="lob-badge" style={{ background: aBg }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: lob.accent, display: 'inline-block', marginRight: 4 }} />
            <span style={{ color: aTc }}>{lob.label}</span>
          </span>
        </div>
        <input className="sel-input list-search" style={{ width: 200 }} placeholder="⌕ Search name, AE, product"
               value={search} onChange={(e) => setSearch(e.target.value)} data-testid="company-search" />
        <select className="sel-input" value={tierFilter} onChange={(e) => setTierFilter(e.target.value)} data-testid="company-tier-filter">
          <option value="">All tiers</option>
          {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <button className="cta-btn" style={{ background: lob.accent }} onClick={openNew} data-testid="add-company-btn">
          <Plus size={11} style={{ marginRight: 4 }} />Add company
        </button>
      </div>

      {err && <div className="module-banner err" data-testid="company-error">{err}</div>}

      <div className="content-area">
        {loading && companies.length === 0 && <div className="empty-state">Loading…</div>}
        {!loading && companies.length === 0 && (
          <div className="empty-state" data-testid="companies-empty">
            <Building2 size={36} style={{ opacity: 0.25 }} />
            <div className="ttl">No companies yet</div>
            <div className="sub">Click <b>Add company</b> to build your partner directory — ranked by Tier.</div>
          </div>
        )}
        {companies.length > 0 && (
          <div className="card">
            <table className="tbl" data-testid="companies-table">
              <thead>
                <tr>
                  <th>Tier</th><th>Company</th><th>Products</th><th>Min FICO</th><th>AE contact</th>
                  <th>Broker</th><th>White label</th><th>HUD</th><th>Active</th><th></th>
                </tr>
              </thead>
              <tbody>
                {companies.map((c) => (
                  <tr key={c.id} data-testid={`company-row-${c.id}`} style={{ cursor: 'pointer' }} onClick={() => openEdit(c)}>
                    <td><span className="tag-pill" style={{ background: `${TIER_COLOR[c.tier]}22`, color: TIER_COLOR[c.tier] }}>{c.tier}</span></td>
                    <td style={{ fontWeight: 600 }}>
                      {c.name}
                      {c.website && (
                        <a href={c.website} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} style={{ marginLeft: 6, color: 'var(--text-faded)' }} data-testid={`company-website-${c.id}`}>
                          <Globe size={10} />
                        </a>
                      )}
                    </td>
                    <td style={{ fontSize: 11 }}>{(c.products || []).join(', ') || '—'}</td>
                    <td>{c.min_fico ?? '—'}</td>
                    <td style={{ fontSize: 11 }}>
                      {[c.ae_first, c.ae_last].filter(Boolean).join(' ') || '—'}
                      {c.ae_phone && <div style={{ color: 'var(--text-faded)' }}>{c.ae_phone}</div>}
                    </td>
                    <td><YesNo value={c.broker_program} /></td>
                    <td><YesNo value={c.white_label} /></td>
                    <td><YesNo value={c.hud_approved} /></td>
                    <td><YesNo value={c.active_partner} /></td>
                    <td className="tbl-actions" onClick={(e) => e.stopPropagation()}>
                      <button className="btn-link" style={{ color: 'var(--error)' }} onClick={() => del(c)} data-testid={`delete-company-${c.id}`}><Trash2 size={11} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <CompanyModal lob={lob} company={editing} onClose={(saved) => { setShowModal(false); if (saved) load(); }} />
      )}
    </>
  );
};

const CompanyModal = ({ lob, company, onClose }) => {
  const [form, setForm] = useState(company || {
    lob_id: lob.id, name: '', website: '', main_phone: '', portal_url: '',
    products: [], min_fico: '', broker_program: false, white_label: false,
    ae_first: '', ae_last: '', ae_phone: '', ae_email: '',
    invoice: false, hud_approved: false, active_partner: true, tier: 'Unranked', notes: '',
  });
  const [productsText, setProductsText] = useState((company?.products || []).join(', '));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    const payload = {
      ...form,
      min_fico: form.min_fico === '' ? null : Number(form.min_fico),
      products: productsText.split(',').map((p) => p.trim()).filter(Boolean),
    };
    try {
      if (company) await api.patch(`/companies/${company.id}`, payload);
      else await api.post('/companies', payload);
      onClose(true);
    } catch (ex) { setErr(formatApiError(ex)); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay-v2" onClick={() => onClose(false)}>
      <form className="modal-card wide" onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="company-modal">
        <div className="modal-title">{company ? `Edit ${company.name}` : 'Add company'}</div>

        <div className="modal-row">
          <div><label className="f-label">Company name</label><input className="f-input" value={form.name} onChange={(e) => set('name', e.target.value)} required data-testid="co-name" /></div>
          <div>
            <label className="f-label">Tier</label>
            <select className="f-select" value={form.tier} onChange={(e) => set('tier', e.target.value)} data-testid="co-tier">
              {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <div className="modal-row">
          <div><label className="f-label">Website</label><input className="f-input" placeholder="https://" value={form.website || ''} onChange={(e) => set('website', e.target.value)} data-testid="co-website" /></div>
          <div><label className="f-label">Main number</label><input className="f-input" value={form.main_phone || ''} onChange={(e) => set('main_phone', e.target.value)} data-testid="co-phone" /></div>
        </div>
        <div className="modal-row">
          <div><label className="f-label">Portal address</label><input className="f-input" placeholder="https://portal…" value={form.portal_url || ''} onChange={(e) => set('portal_url', e.target.value)} data-testid="co-portal" /></div>
          <div><label className="f-label">Minimum FICO</label><input className="f-input" type="number" value={form.min_fico ?? ''} onChange={(e) => set('min_fico', e.target.value)} data-testid="co-fico" /></div>
        </div>

        <label className="f-label">Products / programs offered</label>
        <input className="f-input" placeholder="Fix & Flip, DSCR, Commercial Bridge…" value={productsText} onChange={(e) => setProductsText(e.target.value)} data-testid="co-products" />
        <div style={{ fontSize: 10, color: 'var(--text-faded)', margin: '4px 0 12px' }}>Comma-separated — add as many as you need.</div>

        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', margin: '4px 0 14px' }}>
          <label className="f-checkbox"><input type="checkbox" checked={!!form.broker_program} onChange={() => set('broker_program', !form.broker_program)} data-testid="co-broker" />Broker program</label>
          <label className="f-checkbox"><input type="checkbox" checked={!!form.white_label} onChange={() => set('white_label', !form.white_label)} data-testid="co-whitelabel" />White label</label>
          <label className="f-checkbox"><input type="checkbox" checked={!!form.invoice} onChange={() => set('invoice', !form.invoice)} data-testid="co-invoice" />Invoice</label>
          <label className="f-checkbox"><input type="checkbox" checked={!!form.hud_approved} onChange={() => set('hud_approved', !form.hud_approved)} data-testid="co-hud" />HUD approved</label>
          <label className="f-checkbox"><input type="checkbox" checked={!!form.active_partner} onChange={() => set('active_partner', !form.active_partner)} data-testid="co-active" />Active partner</label>
        </div>

        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-faded)', marginBottom: 8 }}>Account Executive</div>
        <div className="modal-row">
          <div><label className="f-label">First name</label><input className="f-input" value={form.ae_first || ''} onChange={(e) => set('ae_first', e.target.value)} data-testid="co-ae-first" /></div>
          <div><label className="f-label">Last name</label><input className="f-input" value={form.ae_last || ''} onChange={(e) => set('ae_last', e.target.value)} data-testid="co-ae-last" /></div>
        </div>
        <div className="modal-row">
          <div><label className="f-label">Phone</label><input className="f-input" value={form.ae_phone || ''} onChange={(e) => set('ae_phone', e.target.value)} data-testid="co-ae-phone" /></div>
          <div><label className="f-label">Email</label><input className="f-input" type="email" value={form.ae_email || ''} onChange={(e) => set('ae_email', e.target.value)} data-testid="co-ae-email" /></div>
        </div>

        <label className="f-label">Notes</label>
        <textarea className="f-textarea" rows={3} value={form.notes || ''} onChange={(e) => set('notes', e.target.value)} data-testid="co-notes" />

        {err && <div className="f-error" data-testid="co-error">{err}</div>}
        <div className="modal-actions">
          <button type="button" className="btn-secondary" onClick={() => onClose(false)} data-testid="co-cancel">Cancel</button>
          <button type="submit" className="btn-primary" disabled={busy} data-testid="co-submit">{busy ? 'Saving…' : (company ? 'Save' : 'Create')}</button>
        </div>
      </form>
    </div>
  );
};

export default Companies;
